<script>
  import { getContext, onMount } from 'svelte';
  import { format } from 'svelte-i18n';
  import { Link, link, Route, Router, useLocation, useNavigate } from 'svelte-navigator';
  import {
    Breadcrumb,
    BreadcrumbItem,
    Button,
    Col,
    Container,
    Dropdown,
    DropdownItem,
    DropdownMenu,
    DropdownToggle,
    Row
  } from '@sveltestrap/sveltestrap';

  import Redirect from '@client/app/redirect.svelte';

  import { DomainSettingsSearch } from '@client/components/domain-settings-search';
  import { Icon } from '@client/components/icon';
  import { Loader } from '@client/components/loader';
  import { getNotificationsContext } from '@client/components/notifications';
  import { ScrollSpy, SectionHeader } from '@client/components/scrollspy';

  import { DomainService } from '@client/services/domain';
  import { DomainsService } from '@client/services/domains';

  import { hasCapability, userStore } from '@client/stores/user';

  import { deepCopyArray } from '@common/utils/arrays';
  import { merge } from '@common/utils/objects';

  import { Branding } from './branding';
  import { Compliance } from './compliance';
  import { CRM } from './crm';
  import { Deliverabilty } from './deliverability';
  import { Diagnostics } from './diagnostics';
  import { Directory } from './directory';
  import { DOMAIN_CATEGORIES, DOMAIN_SETTINGS_SEARCH } from './domain-config';
  import { General } from './general';
  import { Google } from './google';
  import { Integrations } from './integrations';
  import { Marketing } from './marketing';
  import { Outlook } from './outlook';
  import { default as PowerShell } from './powershell';
  import { Routing } from './routing';
  import { Signature } from './signature';
  import { SSO } from './sso';

  const { addNotification } = getNotificationsContext();

  const location = useLocation();
  const navigate = useNavigate();
  const { basepath } = getContext('config');

  let settings = null;
  let initialSettings = null;
  let loading = true;
  let showPowerScript = false;
  let selectedValue = '';

  const getProps = ({ href, location }) => {
    const isActive = href.replace(basepath, '') === location.pathname || href.includes(location.pathname) || false;

    if (isActive) {
      return {
        class: 'active'
      };
    }

    return {};
  };

  const onClearDomainCache = async () => {
    loading = true;
    await DomainsService.clearDomainCache({ domainname: settings?.domainname });
    loading = false;

    addNotification({
      text: $format('label.SUCCESS.DOMAIN_CACHE_CLEARED'),
      type: 'success',
      autohide: 2500,
      position: 'top-right'
    });
  };

  const onUpdate = async (id, label, value) => {
    if (!id) {
      return;
    }

    try {
      const formattedId = id.replace(/-/g, '.');
      let [parent, property] = formattedId.split('.');
      let newValue = value;
      let update = {};

      if (
        id === 'attTemplateType' ||
        id === 'defaultsyncoptions-replace' ||
        id === 'customdirtokenmethod' ||
        id === 'customdirendpointmethod' ||
        id === 'customdirendpointpostcontenttype' ||
        id === 'customdirtokenpostcontenttype'
      ) {
        newValue = value.value;
      }

      if (id === 'authPolicy' || (parent === 'authPolicy' && property !== null)) {
        if (id === 'authPolicy') {
          newValue = {
            ...initialSettings.authPolicy,
            ...value.reduce((acc, { name, value }) => ({ ...acc, [name]: Number(value) }), {})
          };
        } else {
          newValue = {
            ...initialSettings.authPolicy,
            [property]: newValue
          };
        }

        initialSettings.authPolicy = newValue;
      }

      if (id === 'auditconfig' || (parent === 'auditconfig' && property !== null)) {
        if (id === 'auditconfig') {
          newValue = {
            ...initialSettings.auditconfig,
            ...value.reduce((acc, { name, value }) => ({ ...acc, [name]: Number(value) }), {})
          };
        } else {
          newValue = {
            ...initialSettings.auditconfig,
            [property]: newValue
          };
        }

        initialSettings.auditconfig = newValue;
      }

      if (
        (parent === 'defaultnotificationsettings' ||
          parent === 'defaultusersettings' ||
          parent === 'mysigui' ||
          parent === 'outlookaddin' ||
          parent === 'gmailaddin' ||
          parent === 'personas' ||
          parent === 'billing' ||
          parent === 'csvtree' ||
          parent === 'custompurge' ||
          parent === 'rolebindings' ||
          parent === 'branding' ||
          parent === 'defaultsyncoptions') &&
        property !== null
      ) {
        if (property === 'defaultSignatureMode' || property === 'fontweight' || property === 'headerfontweight') {
          newValue = value.value;
        }

        if (parent === 'personas') {
          newValue = newValue.filter((item) => item.checked && item.value).map((item) => item.value);
        }

        newValue = {
          ...initialSettings[parent],
          [property]: newValue
        };

        initialSettings[parent] = newValue;
      }

      if (id === 'compliancetracking3p') {
        parent = null;

        value.forEach((item) => {
          update[item.value] = item.checked;
        });
      }

      if (id === 'disclaimers') {
        parent = null;

        Object.keys(newValue).forEach((key) => {
          update[key] = newValue[key];
        });
      }

      if (id === 'cname') {
        update.cnames = [...settings.cnames, newValue].filter((value, index, self) => self.indexOf(value) === index);
      }

      if (parent) {
        if (property !== null) {
          update[parent] = newValue;
        } else {
          update[parent][property] = newValue;
        }
      }

      // console.log('newValue after:', formattedId, newValue);
      // console.log(JSON.stringify(update, null, 2));
      // console.log(initialSettings);

      const response = await DomainService.updateSettings(update);

      if (!response.status) {
        addNotification({
          text: $format('label.SUCCESS.SETTING_UPDATED', { values: { setting: label } }),

          type: 'success',
          autohide: 2500,
          position: 'top-right'
        });
      } else {
        addNotification({
          text: $format('label.FAILURE.SETTING_UPDATED', { values: { setting: label } }),
          type: 'error',
          autohide: 2500,
          position: 'top-right'
        });
      }

      if (formattedId === 'googleenabled' || formattedId === 'office365enabled') {
        window.location.reload();
      }
    } catch (error) {
      console.error('Failed to update domain setting:', error);

      addNotification({
        text: $format('label.FAILURE.SETTING_UPDATED', { values: { setting: label } }),
        type: 'error',
        autohide: 2500,
        position: 'top-right'
      });
    }
  };

  const scrollIntoView = () => {
    const { state = { section } } = $location;
    const section = state?.section;
    const id = section || $location.pathname.replace('/manage/domain-settings/', '').replace('/', '-');

    if (id) {
      const element = document.getElementById(id);

      if (element) {
        if (!section) {
          element.firstChild.scrollIntoView();
        } else {
          element.scrollIntoView({
            behavior: 'smooth',
            inline: 'nearest'
          });

          element.classList.add('focus-ring');

          setTimeout(() => element.classList.remove('focus-ring'), 800);
        }
      }
    }
  };

  let categories = [];

  const getCurrentSelection = (id, categories) => {
    return categories
      .map((category) => {
        if (category?.id === id) {
          return category;
        }

        if (category.items) {
          let match = false;

          for (let i = 0, k = category.items.length; i < k; i++) {
            const nested = category.items[i];

            if (nested.id === id) {
              match = nested;
              break;
            }
          }

          if (match) {
            return match;
          }
        }

        return null;
      })
      .filter(Boolean)[0];
  };

  let searchFilterText = '';
  let domainSettingsSearch = deepCopyArray(DOMAIN_SETTINGS_SEARCH);
  let searchItemsFilter = domainSettingsSearch;
  let listOpen = false;

  const onSearchInput = (e) => {
    e.preventDefault();

    listOpen = true;

    const value = e.detail ? e.detail.label.toLowerCase() : '';

    if (value) {
      searchItemsFilter = [...domainSettingsSearch].filter((item) => {
        return !item.groupHeader && JSON.stringify(item).toLowerCase().indexOf(value) > -1;
      });
    } else {
      searchItemsFilter = [...domainSettingsSearch];
    }
  };

  const onSearchResultClick = (selectedItem) => {
    const id = selectedItem.value.split('?')[0];
    const root = id.split('-')[0];

    listOpen = false;

    searchFilterText = '';

    navigate(`${root}#${id}`, { state: { section: id } });
  };

  const initialize = async () => {
    settings = await DomainService.getSettings();

    domainSettingsSearch = deepCopyArray(DOMAIN_SETTINGS_SEARCH).filter(({ permission }) => {
      return (
        !permission ||
        (permission && hasCapability('read', permission)) ||
        (permission === 'microsoft' && (settings?.office365enabled || $userStore.isStaff)) ||
        (permission === 'google' && (settings?.googleenabled || $userStore.isStaff))
      );
    });

    searchItemsFilter = domainSettingsSearch;

    categories = deepCopyArray(DOMAIN_CATEGORIES)
      .map((config) => {
        if (config.items) {
          config.items = config.items
            .map((item) => {
              return (
                (!item.permission ||
                  (item.permission && hasCapability('read', item.permission)) ||
                  (item.permission === 'microsoft' && (settings?.office365enabled || $userStore.isStaff)) ||
                  (item.permission === 'google' && (settings?.googleenabled || $userStore.isStaff))) &&
                item
              );
            })
            .filter(Boolean);

          return (
            config.items.length &&
            (!config.permission || (config.permission && hasCapability('read', config.permission))) &&
            config
          );
        }

        return (!config.permission || (config.permission && hasCapability('read', config.permission))) && config;
      })
      .filter(Boolean);

    const section = $location?.hash.slice(1) || $location?.state?.section;
    const id = section || $location.pathname.replace('/manage/domain-settings/', '').replace('/', '-');

    const currentSection = getCurrentSelection(id, categories);

    loading = true;

    initialSettings = merge({}, settings);

    selectedValue = currentSection || categories[0];

    loading = false;

    setTimeout(scrollIntoView, 0);
  };

  onMount(initialize);

  $: if ($location) {
    setTimeout(scrollIntoView, 0);
  }

  $: console.log($userStore, $userStore.domain);
  $: console.log(initialSettings);
</script>

<div id="domain-settings" class="page">
  <Loader show={loading} backdrop={true} />
  <div class="page-header">
    <Container fluid>
      <Row>
        <Col md="12">
          <Breadcrumb>
            <BreadcrumbItem>
              <a href="{basepath}/manage" use:link>{$format('label.MANAGE')}</a>
            </BreadcrumbItem>
            <BreadcrumbItem active>{$format('label.DOMAIN_SETTINGS')}</BreadcrumbItem>
          </Breadcrumb>
        </Col>
      </Row>
      <Row class="flex-align--center">
        <Col>
          <div class="settings-header">
            <h1 class="page-title" style="white-space: nowrap;">{$format('label.DOMAIN_SETTINGS')}</h1>
            <DomainSettingsSearch
              class="search-settings hide--tablet"
              groupBy={(item) => item.group}
              placeholder={$format('label.PLACEHOLDER.SEARCH_DOMAIN_SETTINGS')}
              bind:value={searchFilterText}
              bind:items={searchItemsFilter}
              bind:filterText={searchFilterText}
              on:filter={() => (listOpen = true)}
              on:input={onSearchInput}
              bind:listOpen
              searchable
            >
              <svelte:fragment slot="list">
                {#each searchItemsFilter as item}
                  <div
                    class="item"
                    class:group={!item.group}
                    role="button"
                    tabindex="0"
                    on:click={() => (item.group ? onSearchResultClick(item) : false)}
                  >
                    <div class="search-item">
                      <div class="search-item-label">{@html $format(item.label)}</div>
                      {#if item.group}
                        <small class="text-muted">{$format(item.group)}</small>
                      {/if}
                      {#if item.description}
                        <p class="search-item-description">{$format(item.description)}</p>
                      {/if}
                    </div>
                  </div>
                {/each}
                {#if !searchItemsFilter.length}
                  <div class="empty">{$format('label.NOTHING_MATCHES_SEARCH_CRITERIA')}</div>
                {/if}
              </svelte:fragment>
            </DomainSettingsSearch>
            <Dropdown class="hide--tablet">
              <DropdownToggle nav>
                {$format('label.INTEGRATION_GUIDES')}
                <Icon name="chevron-down" />
              </DropdownToggle>
              <DropdownMenu end class="min-w-200">
                <DropdownItem header>{$format('label.INTEGRATIONS')}</DropdownItem>
                {#if settings?.office365enabled || $userStore.isStaff}
                  <DropdownItem>
                    <a
                      href="https://login.microsoftonline.com/common/oauth2/authorize?client_id=930d2f75-867f-46bf-a4d9-b18c628fc91b&redirect_uri=https://accounts.opensense.com/auth/azureOAuth2&response_type=code&prompt=admin_consent"
                      target="_blank"
                      rel="noreferrer"
                    >
                      {$format('label.MICROSOFT_365_USER_SYNC_NEW')}
                    </a>
                  </DropdownItem>
                  <DropdownItem>
                    <a
                      href="https://login.microsoftonline.com/common/oauth2/authorize?client_id=ac895851-8cde-46b2-b444-3d0af76e8d51&redirect_url=https://login.sendergen.com/auth/azureOAuth2&response_type=code&prompt=admin_consent"
                      target="_blank"
                      rel="noreferrer"
                    >
                      {$format('label.MICROSOFT_365_OWA_PUSHER')}
                    </a>
                  </DropdownItem>
                  <DropdownItem>
                    <a
                      href="https://outlook.opensense.com/releases/latest/manifest.prod.xml"
                      target="_blank"
                      rel="noreferrer"
                    >
                      {$format('label.MICROSOFT_365_ADDIN_MANIFEST_DEFAULT')}
                    </a>
                  </DropdownItem>
                  <DropdownItem>
                    <a
                      href="https://outlook.opensense.com/releases/mobile/mobile-manifest.xml"
                      target="_blank"
                      rel="noreferrer"
                    >
                      {$format('label.MICROSOFT_365_ADDIN_MANIFEST_MOBILE')}
                    </a>
                  </DropdownItem>
                {/if}
                {#if settings?.o365gcchigh}
                  <DropdownItem>
                    <a
                      href="https://login.microsoftonline.us/common/oauth2/authorize?client_id=a833da31-07b9-4f5e-b898-07a06f89992e&redirect_uri=https://accounts.opensense.com/auth/azureGCCHighOAuth2/callback&response_type=code&prompt=admin_consent"
                      target="_blank"
                      rel="noreferrer"
                    >
                      {$format('label.GCC_HIGH_DIRECTORY_SYNC')}
                    </a>
                  </DropdownItem>
                  <DropdownItem>
                    <a
                      href="https://outlook.opensense.com/releases/gov/gcc-high/manifest.prod.xml"
                      target="_blank"
                      rel="noreferrer"
                    >
                      {$format('label.GCC_HIGH_ADDIN_MANIFEST')}
                    </a>
                  </DropdownItem>
                {/if}
                {#if settings?.googleenabled || $userStore.isStaff}
                  <DropdownItem>
                    <a
                      href="https://gsuite.google.com/marketplace/app/opensense_formerly_sendergen/235509812571?pann=cwsdp&hl=en-US"
                      target="_blank"
                      rel="noreferrer"
                    >
                      {$format('label.GOOGLE_APP')}
                    </a>
                  </DropdownItem>
                  <DropdownItem>
                    <a
                      href="https://workspace.google.com/marketplace/app/opensense_email_signatures/677559048885"
                      target="_blank"
                      rel="noreferrer"
                    >
                      {$format('label.GOOGLE_ADDON')}
                    </a>
                  </DropdownItem>
                {/if}

                <DropdownItem>
                  <a
                    href="https://appexchange.salesforce.com/appxListingDetail?listingId=a0N3A00000ExYHyUAN&tab=e"
                    target="_blank"
                    rel="noreferrer"
                  >
                    {$format('label.SALESFORCE')}
                  </a>
                </DropdownItem>
                {#if settings?.office365enabled || $userStore.isStaff}
                  <DropdownItem divider />
                  <DropdownItem header>{$format('label.MODULES')}</DropdownItem>
                  <DropdownItem on:click={() => (showPowerScript = !showPowerScript)}>
                    {$format('label.MICROSOFT_365_SETUP_POWERSHELL_SCRIPT')}
                  </DropdownItem>
                {/if}
              </DropdownMenu>
            </Dropdown>
            <Button color="secondary" on:click={onClearDomainCache}>
              <Icon name="trash" />
              {$format('label.DOMAIN_CACHE')}
            </Button>
          </div>
        </Col>
      </Row>
    </Container>
  </div>
  {#if !loading}
    <ScrollSpy>
      <div class="page-content">
        <Container fluid class="page-container">
          <Row>
            <Col class="categories-column">
              <div class="categories">
                {#each categories as category}
                  <Link to={category.path} {getProps} on:click={() => (selectedValue = category)}>
                    {$format(category.label)}
                  </Link>
                  {#if category.items}
                    <ul>
                      {#each category.items as section}
                        <li>
                          <Link
                            to={`${category.path}#${section.id}`}
                            state={{ section: section.id }}
                            on:click={() => (selectedValue = section)}
                          >
                            <SectionHeader section={section.id}>
                              {$format(section.label)}
                            </SectionHeader>
                          </Link>
                        </li>
                      {/each}
                    </ul>
                  {/if}
                {/each}
              </div>
            </Col>
            <Col>
              <div class="category-content">
                <div class="category-content-dropdown">
                  <Dropdown>
                    <DropdownToggle>
                      {$format(selectedValue.nestedLabel) || $format(selectedValue.label)}
                      <Icon name="chevron-down" />
                    </DropdownToggle>
                    <DropdownMenu end>
                      {#each categories as category, index}
                        {#if category.items}
                          <DropdownItem header>{$format(category.label)}</DropdownItem>
                          {#each category.items as section}
                            <DropdownItem
                              active={selectedValue.id === section.id}
                              on:click={() => (selectedValue = section)}
                            >
                              <Link to={`${category.path}#${section.id}`} state={{ section: section.id }}>
                                <SectionHeader section={section.id}>
                                  {$format(section.label)}
                                </SectionHeader>
                              </Link>
                            </DropdownItem>
                          {/each}
                        {:else}
                          <DropdownItem
                            active={selectedValue.id === category.id}
                            on:click={() => (selectedValue = category)}
                          >
                            <Link to={category.path}>{$format(category.label)}</Link>
                          </DropdownItem>
                        {/if}
                        {#if index < DOMAIN_CATEGORIES.length - 1}
                          <DropdownItem divider />
                        {/if}
                      {/each}
                    </DropdownMenu>
                  </Dropdown>
                </div>
                <div class="category-content-configs">
                  <Router>
                    <Route path="general">
                      <General bind:settings {onUpdate} {categories} />
                    </Route>
                    <Route path="branding">
                      <Branding bind:settings {onUpdate} {categories} />
                    </Route>
                    <Route path="routing">
                      <Routing bind:settings {onUpdate} {categories} />
                    </Route>
                    <Route path="deliverabilty">
                      <Deliverabilty bind:settings {onUpdate} {categories} />
                    </Route>
                    <Route path="sso">
                      <SSO bind:settings {onUpdate} {categories} />
                    </Route>
                    <Route path="crm">
                      <CRM bind:settings {onUpdate} {categories} />
                    </Route>
                    <Route path="marketing">
                      <Marketing bind:settings {onUpdate} {categories} />
                    </Route>
                    <Route path="compliance">
                      <Compliance bind:settings {onUpdate} {categories} />
                    </Route>
                    <Route path="outlook">
                      <Outlook bind:settings {onUpdate} {categories} />
                    </Route>
                    <Route path="google">
                      <Google bind:settings {onUpdate} {categories} />
                    </Route>
                    <Route path="signature">
                      <Signature bind:settings {onUpdate} {categories} />
                    </Route>
                    <Route path="directory">
                      <Directory bind:settings {onUpdate} {categories} />
                    </Route>
                    <Route path="integrations">
                      <Integrations bind:settings {onUpdate} {categories} />
                    </Route>
                    <Route path="diagnostics">
                      <Diagnostics />
                    </Route>
                    <Route path="/*">
                      {#if $userStore.isStaff}
                        <Redirect path="{basepath}/manage/domain-settings/general" config={{ replace: true }} />
                      {:else}
                        <Redirect path="{basepath}/manage/domain-settings/directory" config={{ replace: true }} />
                      {/if}
                    </Route>
                  </Router>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    </ScrollSpy>
  {/if}

  <PowerShell
    bind:show={showPowerScript}
    domainname={settings?.domainname}
    internaldomains={settings?.internaldomains}
  />
</div>

<style lang="scss" src="./domain-settings.scss"></style>
